import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Row, DropdownButton } from 'react-bootstrap'
import { formatRelative } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperclip, faEllipsisV } from '@fortawesome/pro-solid-svg-icons'

import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import { getLanguage } from 'helpers/date.helper'
import { useSelector } from 'react-redux'
import { IMainState } from 'store/states'
import styles from './ChatMessages.module.scss'

interface IChatMessages {
  onDelete: any
  messages: any[]
  fromModal?: boolean
}

const ChatMessages: React.FC<IChatMessages> = ({
  onDelete,
  messages,
  fromModal = 'false',
}) => {
  const { t } = useTranslation()
  const listContainerRef: any = useRef()
  const messageList: any = useRef([])
  const { user } = useSelector(({ user }: IMainState) => user)

  useEffect(() => {
    if (messages.length > messageList.current.length)
      listContainerRef.current.scrollTop = listContainerRef.current.scrollHeight
    messageList.current = messages
  }, [messages])

  const linkify = (text: string) => {
    const urlRegex =
      /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi
    return text.replace(urlRegex, (url) => {
      return '<a href="' + url + '" target="_blank">' + url + '</a>'
    })
  }

  return (
    <div
      className={`w-100 ${styles.heightFix}  ${
        !fromModal ? styles.unprofesionalFix : ''
      }`}
    >
      {!fromModal && (
        <small className={styles.legend}>
          {t('components.chat.emptyMessageListUser')}
        </small>
      )}
      <div
        ref={listContainerRef}
        className={`messages-container ${styles.messagesContainer}`}
      >
        {messages.map(
          (
            {
              messageId,
              from,
              content,
              attachments: { data: attachments },
              createdAt,
            }: any,
            index: number
          ) => (
            <div
              className={`mt-3 pt-1 position-relative`}
              key={messageId + '-message-' + index}
            >
              <Row>
                <Col xs={9} className={styles.avatar}>
                  <UserAvatar user={from} />
                </Col>
                <Col className="d-flex justify-content-end pt-2">
                  <small className={styles.createAt}>
                    {' '}
                    {formatRelative(new Date(createdAt), new Date(), {
                      locale: getLanguage(),
                    })}{' '}
                  </small>
                  {from.identifier === user.identifier && (
                    <DropdownButton
                      alignRight
                      title={
                        <FontAwesomeIcon
                          icon={faEllipsisV}
                          size="1x"
                          className="mr-2"
                        />
                      }
                      id="dropdown-menu-align-right"
                      className={styles.actionSelect}
                    >
                      <a onClick={() => onDelete(messageId)}>
                        {t('components.chat.deleteMessage')}
                      </a>
                    </DropdownButton>
                  )}
                </Col>
              </Row>
              {content && (
                <div>
                  <div
                    className={styles.message}
                    dangerouslySetInnerHTML={{
                      __html: linkify(sanitizeHtml(content)),
                    }}
                  />
                </div>
              )}
              <div>
                {attachments.map(
                  (
                    {
                      attributes: {
                        fileData: {
                          metadata: { filename },
                        },
                        url,
                      },
                    }: any,
                    fileIndex: number
                  ) => (
                    <a
                      key={
                        messageId + '-message-' + index + '-file-' + fileIndex
                      }
                      className={`mr-2 mt-2 ${styles.file}`}
                      href={url}
                      target="_blank"
                    >
                      <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                      <span>{filename}</span>
                    </a>
                  )
                )}
              </div>
              <hr className={styles.divider} />
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default ChatMessages
